import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Loader from "components/loader/Loader";
import { ErrorState } from "components/states";
import { calculateAssessmentResults } from "utils/assessmentScoring";
import PublicSurveyShell from "views/public/shared/PublicSurveyShell";
import SurveyProgress from "views/public/shared/SurveyProgress";
import { PUBLIC_AUDIENCES } from "views/public/shared/audiences";
import { isRetryableError } from "views/public/shared/errors";
import { applyAnswer, buildResponses, organizationOf, resolveQuestions } from "./questions";
import AssessmentIntro from "./AssessmentIntro";
import AssessmentQuestion from "./AssessmentQuestion";
import AssessmentResults from "./AssessmentResults";
import ProfileSetupStep from "./ProfileSetupStep";
import ProfileVerifyStep from "./ProfileVerifyStep";

/**
 * Public intake assessment opened from a shared link: /staff-intake/:slug and
 * /volunteer-intake/:slug. The organization comes from the subdomain (X-Church-Slug).
 *
 * Phases: intro → profile → verify → questions → submitting → results.
 */
const IntakePage = ({ audience }) => {
  const { service } = PUBLIC_AUDIENCES[audience];
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);
  const [submissionToken, setSubmissionToken] = useState(null);

  const [phase, setPhase] = useState("intro");
  const [profileData, setProfileData] = useState(null);
  const [profileSubmitting, setProfileSubmitting] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({ partA: {}, partB: {} });
  const [results, setResults] = useState(null);

  const questions = useMemo(() => resolveQuestions(form), [form]);
  const totalQuestions = questions.length;
  const organization = organizationOf(form);

  const fetchForm = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const response = await service.getPublicIntakeForm(slug);
      if (response.success) {
        setForm(response.data);
      } else {
        setError({ message: "Assessment not found or is no longer active.", retryable: false });
      }
    } catch (err) {
      console.error("Error loading form:", err);
      setError({
        message: "Unable to load assessment. Please try again later.",
        retryable: isRetryableError(err),
      });
    } finally {
      setLoading(false);
    }
  }, [service, slug]);

  useEffect(() => {
    fetchForm();
  }, [fetchForm]);

  const startAssessment = useCallback(async () => {
    try {
      const response = await service.startIntakeSubmission(slug, {});
      if (response.success) {
        setSubmissionToken(response.data.submissionToken);
      } else {
        console.error("Failed to start intake submission:", response);
      }
    } catch (err) {
      console.error("Error starting intake submission:", err);
    }
    setPhase("profile");
  }, [service, slug]);

  const handleProfileComplete = useCallback((data) => {
    const avatarPreview = data.avatar ? URL.createObjectURL(data.avatar) : null;
    setProfileData({ ...data, avatarPreview });
    setPhase("verify");
  }, []);

  const handleVerifyConfirm = useCallback(async () => {
    // The assessment continues even when the profile cannot be saved.
    if (submissionToken && profileData) {
      setProfileSubmitting(true);
      try {
        const response = await service.saveProfile(submissionToken, profileData);
        if (!response.success) console.error("Failed to save profile:", response.error);
      } catch (err) {
        console.error("Error saving profile:", err);
      } finally {
        setProfileSubmitting(false);
      }
    } else {
      console.error("No submission token or profile data available");
    }
    setPhase("questions");
    setCurrentIndex(0);
  }, [service, submissionToken, profileData]);

  const finishAssessment = useCallback(async () => {
    setPhase("submitting");
    setResults(calculateAssessmentResults(answers, questions));

    try {
      const frameworkId = form?.assessmentFramework?.id || form?.assessmentFrameworkId;
      if (submissionToken && frameworkId) {
        await service.saveAssessmentResponses(submissionToken, frameworkId, buildResponses(questions, answers));
      }
    } catch (err) {
      console.error("Error submitting assessment:", err);
    }

    setPhase("results");
  }, [service, answers, submissionToken, form, questions]);

  const handleAnswer = useCallback(
    (value) => {
      const question = questions[currentIndex];
      if (!question) return;

      setAnswers((prev) => applyAnswer(prev, question, value));

      // Auto-advance after a short pause so the selection registers visually.
      setTimeout(() => {
        if (currentIndex < totalQuestions - 1) {
          setCurrentIndex((prev) => prev + 1);
        } else {
          finishAssessment();
        }
      }, 400);
    },
    [currentIndex, questions, totalQuestions, finishAssessment]
  );

  const shellProps = { organizationName: organization.name, organizationLogo: organization.logo };

  if (loading) {
    return (
      <PublicSurveyShell center>
        <Loader size="lg" label="Loading assessment" />
      </PublicSurveyShell>
    );
  }

  if (error) {
    return (
      <PublicSurveyShell center>
        <ErrorState
          title="Assessment Unavailable"
          titleAs="h1"
          message={error.message}
          onRetry={error.retryable ? fetchForm : undefined}
        />
      </PublicSurveyShell>
    );
  }

  if (phase === "intro") {
    return (
      <PublicSurveyShell {...shellProps} center>
        <AssessmentIntro organizationName={organization.name} onBegin={startAssessment} />
      </PublicSurveyShell>
    );
  }

  if (phase === "profile") {
    return (
      <PublicSurveyShell {...shellProps}>
        <ProfileSetupStep
          organizationName={organization.name}
          onComplete={handleProfileComplete}
          initialData={profileData}
        />
      </PublicSurveyShell>
    );
  }

  if (phase === "verify") {
    return (
      <PublicSurveyShell {...shellProps}>
        <ProfileVerifyStep
          profileData={profileData}
          onConfirm={handleVerifyConfirm}
          onEdit={() => setPhase("profile")}
          isSubmitting={profileSubmitting}
        />
      </PublicSurveyShell>
    );
  }

  if (phase === "questions" && questions[currentIndex]) {
    return (
      <PublicSurveyShell
        {...shellProps}
        progress={<SurveyProgress current={currentIndex + 1} total={totalQuestions} />}
      >
        <AssessmentQuestion
          key={questions[currentIndex].id}
          question={questions[currentIndex]}
          answers={answers}
          onAnswer={handleAnswer}
        />
      </PublicSurveyShell>
    );
  }

  if (phase === "results" && results) {
    return (
      <PublicSurveyShell {...shellProps} panel={false}>
        <AssessmentResults
          results={results}
          organizationName={organization.name}
          userName={profileData?.firstName}
        />
      </PublicSurveyShell>
    );
  }

  return (
    <PublicSurveyShell {...shellProps} center>
      <Loader size="lg" label="Calculating your results" />
      <p aria-hidden="true" className="text-center text-sm text-fg-muted">Calculating your results…</p>
    </PublicSurveyShell>
  );
};

export default IntakePage;
